import { scaleW } from '@/common/style';
import { KImage, KScrollView, KText, KView } from '@/component/RtlLayout';
import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, TouchableWithoutFeedback, View, Text, Animated } from 'react-native';
import EnlargePng from '@/assets/icon_detail/enlarge.png';
import { logClickSelectNumber, logShowMessage } from '../../../logger2.0';
import { Toast } from '@kibt/meta-ui-krn';
import { $k18n } from '@/common/i18n';
import ReduceUnablePng from '@/assets/icon_detail/reduce_unable.png';
import ReducePng from '@/assets/icon_detail/reduce.png';
import IncreaseUnablePng from '@/assets/icon_detail/increase_unable.png';
import IncreasePng from '@/assets/icon_detail/increase.png';
import { useSlideBack } from '@/hooks/useSlideBack';
import { useThrottleFn } from 'ahooks';
import { PageRowData, Sku } from '../../../api';
import { KTouchableWithoutFeedback } from '@/component/RtlLayout/Touchable';
import { isSmallDeviceWidth } from '@/common/device-info';

interface SpecSelectAreaProp {
    specImageIndex: number;
    selectionList: any;
    pageRawData: PageRowData;
    selectedKV: { [key: string]: number };
    selectItem: Function;
    triggerImagePreview: Function;
    immediateAvailableQuantity: number;
    currentSku: Sku;
    purchaseQuantity: number;
    setPurchaseQuantity: Function;
    showChangeCount: boolean;
    showUpStepper: boolean;
    isPopVisible: boolean;
}

// 渲染规格半屏组件的规格选择区域，其中主规格如果有图片，展示成可以滑动的大图，而不是文字规格
export const RenderSpecSelectArea = (props: SpecSelectAreaProp) => {
    const {
        selectionList,
        specImageIndex,
        pageRawData,
        selectedKV,
        selectItem,
        triggerImagePreview,
        immediateAvailableQuantity,
        currentSku,
        purchaseQuantity,
        setPurchaseQuantity,
        showChangeCount,
        isPopVisible,
    } = props;
    const mainType = selectionList?.[0] || {};
    const otherType = selectionList?.slice(1) || [];
    const minNum = (pageRawData?.minPurchase as number) ?? 1;
    // 这里的宽度是 每列宽度*列数 + padding的宽度 + 精度处理的宽度1
    const width =
        mainType?.content?.length > 6 ? Math.ceil(mainType?.content?.length / 2) * Math.ceil(scaleW(128)) + 33 : 3 * Math.ceil(scaleW(128)) + 33;
    const mainTypeScrollViewRef = useRef<ScrollView>();
    useEffect(() => {
        if (specImageIndex === -1) return;
        const mainTypeCount = pageRawData?.specs?.[0]?.itemSpecValues?.length || 0;
        const columns = Math.ceil(mainTypeCount / 2);
        mainTypeScrollViewRef.current?.scrollTo({ x: ((specImageIndex % columns) - 1) * scaleW(128) });
    }, [specImageIndex, pageRawData?.specs, isPopVisible]);

    const isActive = (item, type) => {
        const key = type?.value;
        const value = item?.value;
        return key && value && selectedKV[key] === value;
    };

    const skuInfo = (
        pageRawData.skus
            ? pageRawData.skus.map((sku: { skuSpecs: any[] }) => {
                  const skuInfoItem = {};
                  sku.skuSpecs.forEach(spec => {
                      skuInfoItem[spec.specId] = spec.specValueId;
                  });
                  return {
                      ...skuInfoItem,
                      ...sku,
                  };
              })
            : []
    ) as Sku[];

    const isDisabled = (item, type) => {
        // 自营测品 放开限制
        if (pageRawData?.extraInfo?.selfOperated && type === 'cart') {
            return false;
        }
        const key = type?.value;
        const value = item?.value;
        const selectedReplaced = JSON.parse(JSON.stringify(selectedKV));
        if (key) selectedReplaced[key] = value;

        const flag = skuInfo.some(skuItem => {
            return Object.keys(selectedKV).every(key => {
                if (selectedReplaced[key]) {
                    // 库存小于最低加购时，不能选
                    return skuItem[key] === selectedReplaced[key] && skuItem.availableQuantity >= minNum;
                }
                return true;
            });
        });

        return !flag;
    };

    const getSpecImageUrls = () => {
        const productMainImage = pageRawData && pageRawData.imageUrls && pageRawData.imageUrls[0];
        // 图片优化的主图
        const productMainImageOpt = pageRawData && pageRawData.templateImageUrls && pageRawData.templateImageUrls[0];
        const imageArr = [productMainImageOpt || productMainImage];

        if (pageRawData?.specs?.[0]?.itemSpecValues?.[0]?.imageUrl && pageRawData?.specs?.[0]?.itemSpecValues?.[0]?.imageUrl !== '')
            pageRawData.specs[0].itemSpecValues?.forEach(item => {
                imageArr.push(item.templateImageUrl || item.imageUrl);
            });

        return [...imageArr];
    };

    const specImageUrls = getSpecImageUrls();

    const descPurchaseQuantity = () => {
        // logClickBuyNow(1);
        logClickSelectNumber('0');
        if (purchaseQuantity !== 1 && purchaseQuantity <= minNum) {
            Toast.show({ text: $k18n({ id: 'k_251870', text: '最少购买{x}件', params: { x: minNum } }) });
            return;
        }
        if (purchaseQuantity === 1) {
            logShowMessage(pageRawData.itemId, 1);
            Toast.show({
                text: $k18n('商品不能再少啦'),
                duration: 1000,
            });
            return;
        }
        const newPurchaseQuantity = purchaseQuantity - 1;
        setPurchaseQuantity(newPurchaseQuantity);
    };

    const addPurchaseQuantity = () => {
        if (!(currentSku && currentSku.skuId)) {
            Toast.show({ text: $k18n({ id: 'k_471961', text: '请先选择规格' }) });
            return;
        }
        // 如果库存为0 或者库存小于最低加购数量
        if (Number(immediateAvailableQuantity) === 0 || Number(immediateAvailableQuantity) < minNum) {
            Toast.show({
                text: $k18n('抱歉，商品已售罄'),
                duration: 1000,
            });
            return;
        }
        logClickSelectNumber('1');
        // 如果选中了具体的一个sku， 则购买上限不能超过库存
        if (currentSku && currentSku.availableQuantity) {
            if (purchaseQuantity >= currentSku.availableQuantity) {
                setPurchaseQuantity(currentSku.availableQuantity);
                Toast.show({
                    text: $k18n('商品数量超出范围'),
                    duration: 1000,
                });
                logShowMessage(pageRawData.itemId, 2);
                return;
            }
        }
        // 有秒杀活动时，如果秒杀限购库存小于商品库存，单独toast限购提示文案
        if (
            pageRawData?.activityInfo?.skuLimitCount?.[currentSku?.skuId] > 0 &&
            pageRawData?.activityInfo?.skuLimitCount?.[currentSku?.skuId] < immediateAvailableQuantity &&
            purchaseQuantity >= pageRawData?.activityInfo?.skuLimitCount?.[currentSku?.skuId]
        ) {
            Toast.show({
                text: $k18n({ id: 'k_367077', params: { num: pageRawData?.activityInfo?.skuLimitCount?.[currentSku?.skuId] }, text: '累计限购XXX' }),
                duration: 1000,
            });
            return;
        }
        if (purchaseQuantity >= Number(immediateAvailableQuantity)) {
            setPurchaseQuantity(Number(immediateAvailableQuantity));
            Toast.show({
                text: $k18n('商品数量超出范围'),
                duration: 1000,
            });
            return;
        }
        const newPurchaseQuantity = purchaseQuantity + 1;
        setPurchaseQuantity(newPurchaseQuantity);
    };

    const [scrollProgress, setScrollProgress] = useState(0);
    const { run: handleScroll } = useThrottleFn(
        event => {
            if (!event.nativeEvent) return;
            const scrollViewContentWidth = event.nativeEvent.contentSize.width; // ScrollView内容的宽度
            const scrollViewContainerWidth = event.nativeEvent?.layoutMeasurement?.width; // ScrollView的宽度
            const scrollPosition = event.nativeEvent.contentOffset.x; // 当前水平滚动的位置

            const currentProgress = scrollPosition / (scrollViewContentWidth - scrollViewContainerWidth);
            setScrollProgress(currentProgress);
        },
        { wait: 16, leading: true }
    );
    const moveAnimation = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        // 在组件加载后开始动画
        Animated.timing(moveAnimation, {
            toValue: scaleW(40) * scrollProgress, // 终点位置
            duration: 16, // 动画时长，单位是毫秒
            useNativeDriver: true, // 使用原生动画驱动
        }).start();
    }, [moveAnimation, scrollProgress]);

    const { setSlideBack } = useSlideBack();

    return (
        <KScrollView style={{"flex":1}} showsVerticalScrollIndicator={false} showsHorizontalScrollIndicator={false}>
            <KView key={0} style={{"position":"relative"}}>
                <KView
                    rtlClassName={'type_title'}
                    style={{"paddingHorizontal":"4.5893719806763285rem"}}>
                    <KText rtlClassName={'type_title_text'}>{mainType.label}</KText>
                </KView>
                <ScrollView
                    horizontal={true}
                    style={{"width":"100%"}}
                    ref={mainTypeScrollViewRef}
                    scrollEventThrottle={16}
                    onScroll={handleScroll}
                    onTouchStart={() => setSlideBack(false)}
                    onTouchEnd={() => setSlideBack(true)}
                    showsVerticalScrollIndicator={false}
                    showsHorizontalScrollIndicator={false}>
                    <KView
                        rtlClassName={'type_options'}
                        style={{
                            paddingRight: scaleW(10),
                            paddingLeft: scaleW(20),
                            marginTop: -scaleW(4),
                            width,
                        }}>
                        {mainType.content.map((item, index) => {
                            return (
                                <KTouchableWithoutFeedback onPress={() => selectItem(item, mainType, 0, index, isDisabled(item, mainType))}>
                                    <KView
                                        rtlClassName={[isActive(item, mainType) ? 'image-option__active' : '']}
                                        style={{
                                            marginRight: scaleW(12),
                                            borderRadius: scaleW(8),
                                            overflow: 'hidden',
                                            width: isSmallDeviceWidth ? scaleW(102) : scaleW(116),
                                            position: 'relative',
                                            marginTop: scaleW(12),
                                            borderWidth: scaleW(1),
                                            borderColor: isActive(item, mainType) ? '#FF4906' : 'transparent',
                                            opacity: isDisabled(item, mainType) ? 0.5 : 1,
                                        }}>
                                        <View
                                            style={{"position":"absolute","top":"0.9661835748792271rem","right":"0.9661835748792271rem","zIndex":1}}>
                                            <KTouchableWithoutFeedback
                                                onPress={() => {
                                                    if (specImageIndex !== index) {
                                                        selectItem(item, mainType, 0, index, isDisabled(item, mainType));
                                                    }
                                                    triggerImagePreview();
                                                }}>
                                                <KImage
                                                    style={{"width":"5.797101449275363rem","height":"5.797101449275363rem"}}
                                                    source={EnlargePng}
                                                />
                                            </KTouchableWithoutFeedback>
                                        </View>
                                        <KImage
                                            source={{ uri: specImageUrls[index + 1] }}
                                            style={{
                                                width: '100%',
                                                height: isSmallDeviceWidth ? scaleW(102) : scaleW(116),
                                            }}
                                        />
                                        <View
                                            style={{
                                                width: '100%',
                                                paddingHorizontal: scaleW(8),
                                                paddingVertical: scaleW(6),
                                                backgroundColor: isActive(item, mainType) ? 'rgba(255, 73, 6, 0.12)' : '#F2F2F2',
                                                height: scaleW(40),
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            <Text
                                                numberOfLines={2}
                                                ellipsizeMode="tail"
                                                style={{
                                                    fontSize: scaleW(12),
                                                    fontWeight: '400',
                                                    lineHeight: scaleW(14),
                                                    textAlign: 'center',
                                                    color: isActive(item, mainType) ? '#FF4906' : '#000000A3',
                                                }}>
                                                {item.label}
                                            </Text>
                                        </View>
                                    </KView>
                                </KTouchableWithoutFeedback>
                            );
                        })}
                    </KView>
                </ScrollView>
                {mainType?.content?.length > 6 && (
                    <View
                        style={{"width":"100%","alignItems":"center","height":"4.830917874396135rem","justifyContent":"center","marginTop":"2.4154589371980677rem"}}>
                        <View
                            style={{"width":"19.32367149758454rem","height":"0.48309178743961356rem","borderRadius":"0.48309178743961356rem","backgroundColor":"#F2F2F2","overflow":"hidden","position":"relative"}}>
                            <Animated.View
                                style={{
                                    ...{
                                        width: scaleW(40),
                                        height: '100%',
                                        borderRadius: 2,
                                        backgroundColor: '#1F1F1F',
                                        overflow: 'hidden',
                                        // translateX: scaleW(40) * scrollProgress,
                                        transform: [{ translateX: moveAnimation }],
                                    },
                                }}
                            />
                        </View>
                    </View>
                )}
            </KView>
            {otherType.length
                ? otherType.map((type, _level) => {
                      const level = _level + 1;
                      return (
                          <KView
                              key={level}
                              style={{"position":"relative","paddingHorizontal":"4.830917874396135rem"}}>
                              <KView rtlClassName={'type_title'}>
                                  <KText rtlClassName={'type_title_text'}>{type.label}</KText>
                              </KView>
                              <KView rtlClassName={'type_options'}>
                                  {type.content.map((item, index) => {
                                      return (
                                          <KTouchableWithoutFeedback onPress={() => selectItem(item, type, level, index, isDisabled(item, type))}>
                                              <KView rtlClassName={['type_option', isActive(item, type) ? 'type-option__active' : '']}>
                                                  <KText
                                                      rtlClassName={[
                                                          'type_option_text',
                                                          isActive(item, type) ? 'type-text_active' : '',
                                                          isDisabled(item, type) ? 'type-option__disabled' : '',
                                                      ]}>
                                                      {item.label}
                                                  </KText>
                                              </KView>
                                          </KTouchableWithoutFeedback>
                                      );
                                  })}
                              </KView>
                          </KView>
                      );
                  })
                : null}
            {showChangeCount && !pageRawData?.extraInfo?.selfOperated ? (
                <KView
                    rtlClassName={'optionbar_num'}
                    style={{"borderColor":"#f2f2f2","borderTopWidth":1}}>
                    <KView rtlClassName={'optionbar_num_left'}>
                        <KText rtlClassName={'optionbar_num_left-text'}>{$k18n('数量')}</KText>
                    </KView>
                    <KView rtlClassName={'optionbar_num_right'}>
                        {purchaseQuantity <= minNum ? (
                            <KTouchableWithoutFeedback onPress={() => descPurchaseQuantity()}>
                                <KView>
                                    <KImage source={ReduceUnablePng} style={{"width":"4.830917874396135rem","height":"4.830917874396135rem"}} />
                                </KView>
                            </KTouchableWithoutFeedback>
                        ) : (
                            <KTouchableWithoutFeedback onPress={() => descPurchaseQuantity()}>
                                <KView>
                                    <KImage source={ReducePng} style={{"width":"4.830917874396135rem","height":"4.830917874396135rem"}} />
                                </KView>
                            </KTouchableWithoutFeedback>
                        )}
                        <KView
                            style={{"borderRadius":"1.9323671497584543rem","overflow":"hidden","marginLeft":"1.9323671497584543rem","marginRight":"1.9323671497584543rem","backgroundColor":"rgba(200, 200, 200, 0.24)","width":"11.594202898550726rem"}}>
                            <KText
                                rtlClassName={'optionbar_num_right_num'}
                                style={{"borderRadius":"1.9323671497584543rem"}}>
                                {purchaseQuantity}
                            </KText>
                        </KView>
                        {(currentSku.availableQuantity && purchaseQuantity === currentSku.availableQuantity) ||
                        Number(immediateAvailableQuantity) === 0 ||
                        Number(immediateAvailableQuantity) <= minNum ? (
                            <KTouchableWithoutFeedback onPress={() => addPurchaseQuantity()}>
                                <KView>
                                    <KImage source={IncreaseUnablePng} style={{"width":"4.830917874396135rem","height":"4.830917874396135rem"}} />
                                </KView>
                            </KTouchableWithoutFeedback>
                        ) : (
                            <KTouchableWithoutFeedback onPress={() => addPurchaseQuantity()}>
                                <KView>
                                    <KImage source={IncreasePng} style={{"width":"4.830917874396135rem","height":"4.830917874396135rem"}} />
                                </KView>
                            </KTouchableWithoutFeedback>
                        )}
                    </KView>
                </KView>
            ) : null}
        </KScrollView>
    );
};
